import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Icon,
  IconButton,
  CircularProgress,
  alpha,
  TextField,
  Alert,
} from "@mui/material";

interface RejectionReasonDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  loading?: boolean;

  // Generic identification props
  requestId?: string;
  itemName?: string;

  // Customization props
  title?: string;
  message?: string;
  placeholder?: string;
  confirmButtonText?: string;
  loadingText?: string;
  notificationMessage?: string;

  // ID label customization (e.g., "Request ID", "Adjustment ID", etc.)
  idLabel?: string;
}

export const RejectionReasonDialog: React.FC<RejectionReasonDialogProps> = ({
  open,
  onClose,
  onConfirm,
  loading = false,
  requestId,
  itemName,
  title = "Reject Request",
  message = "You are about to reject this request. Please provide a clear reason to help understand why it was rejected.",
  placeholder = "Please provide a detailed reason for rejecting this request...",
  confirmButtonText = "Reject Request",
  loadingText = "Rejecting...",
  notificationMessage = "The requester will be notified about this rejection with your reason.",
  idLabel = "Request ID",
}) => {
  const [reason, setReason] = React.useState("");
  const [error, setError] = React.useState("");

  const handleClose = () => {
    if (!loading) {
      setReason("");
      setError("");
      onClose();
    }
  };

  const handleConfirm = () => {
    if (!reason.trim()) {
      setError("Please provide a reason for rejection");
      return;
    }
    if (reason.trim().length < 10) {
      setError("Reason must be at least 10 characters long");
      return;
    }
    setError("");
    onConfirm(reason.trim());
  };

  // Keyboard shortcut handler
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!open || loading) return;

      switch (event.key) {
        case "Escape":
          event.preventDefault();
          handleClose();
          break;
        case "Enter":
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            if (reason.trim().length >= 10) {
              handleConfirm();
            }
          }
          break;
      }
    };

    if (open) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, loading, reason, handleClose, handleConfirm]);

  const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReason(event.target.value);
    if (error && event.target.value.trim().length >= 10) {
      setError("");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2,
          background: "#DC2626",
          color: "white",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Icon sx={{ fontSize: 20 }}>cancel</Icon>
            <Typography variant="subtitle1" fontWeight={600}>
              {title}
            </Typography>
          </Box>
          {!loading && (
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{
                color: "white",
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              <Icon>close</Icon>
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: "24px !important" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: alpha("#DC2626", 0.1),
                color: "#DC2626",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <Icon sx={{ fontSize: 32 }}>cancel</Icon>
            </Box>

            <Typography
              variant="body1"
              sx={{
                color: "text.primary",
                fontFamily: "Montserrat",
                fontSize: "1rem",
                lineHeight: 1.6,
                mb: 1,
              }}
            >
              {message}
            </Typography>

            {requestId && (
              <Typography
                variant="subtitle2"
                sx={{
                  color: "text.secondary",
                  fontWeight: 600,
                  mt: 2,
                }}
              >
                {idLabel}: {requestId}
              </Typography>
            )}

            {itemName && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 1,
                }}
              >
                {itemName}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 600,
                mb: 1,
                color: "text.primary",
              }}
            >
              Reason for Rejection *
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={reason}
              onChange={handleReasonChange}
              placeholder={placeholder}
              error={!!error}
              helperText={error || "Minimum 10 characters required"}
              disabled={loading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  fontFamily: "Montserrat",
                },
                "& .MuiFormHelperText-root": {
                  fontFamily: "Montserrat",
                },
              }}
            />
          </Box>

          <Alert
            severity="error"
            sx={{
              borderRadius: 2,
              "& .MuiAlert-icon": {
                fontSize: "1.25rem",
              },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {notificationMessage}
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          pt: 2,
          bgcolor: "#F8FAFC",
          borderTop: "1px solid",
          borderColor: "divider",
          gap: 1,
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        {/* Subtle keyboard shortcuts hint */}
        <Typography
          variant="caption"
          sx={{
            color: "text.disabled",
            textAlign: "center",
            fontSize: "0.7rem",
            mb: 1,
            fontFamily: "monospace",
          }}
        >
          Press Ctrl+Enter to submit • Esc to cancel
        </Typography>

        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            color="inherit"
            startIcon={<Icon>close</Icon>}
            disabled={loading}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              px: 3,
              py: 1,
              borderColor: "#E5E7EB",
              color: "#64748B",
              fontFamily: "Montserrat",
              fontWeight: 500,
              "&:hover": {
                borderColor: "#94A3B8",
                bgcolor: "transparent",
              },
            }}
            title="Cancel (Esc)"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={loading || !reason.trim() || reason.trim().length < 10}
            startIcon={
              loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Icon>cancel</Icon>
              )
            }
            sx={{
              borderRadius: 2,
              textTransform: "none",
              px: 3,
              py: 1,
              bgcolor: "#DC2626",
              fontFamily: "Montserrat",
              fontWeight: 600,
              "&:hover": {
                bgcolor: "#B91C1C",
              },
              "&.Mui-disabled": {
                bgcolor: alpha("#DC2626", 0.6),
                color: "white",
              },
            }}
            title="Submit rejection (Ctrl+Enter)"
          >
            {loading ? loadingText : confirmButtonText}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
